import type { DbClient } from '../../../../lib/db.js';
import type { CompanyListRow } from '../../../contracts/schemas/companies-list.js';
import {
  buildCompaniesListFilterClause,
  type CompaniesListFilterParams,
  type FilterColumn,
} from './companies-list-filter.js';
import { buildProfileColumnsLateral } from './companies-list-sql.js';
import {
  type RawCompanyListRow,
  transformCompanyListRow,
} from './companies-list-transform.js';

export type ListCompaniesNetworkParams = CompaniesListFilterParams & {
  page: number;
  count: number;
  sort?: string;
  isDropDown?: boolean;
  hasRiskV3Enabled: boolean;
};

export interface ListCompaniesNetworkResult {
  rows: CompanyListRow[];
  totalCount: number;
  pageSize: number;
}

/** Mirrors the legacy `PG_FILTER_SCOPED` map. */
const FILTER_COLUMNS: Record<string, FilterColumn> = {
  name: { expr: 'company_property.display_name', useLike: true },
  country: { expr: 'company_property.country', arrayable: true },
  facilityProcesses: {
    expr: `(COALESCE((company_profiles.data->'facility_processes'->>'value'), (company_profiles.data->'facility_processes'->'value')::text))`,
    useJsonRegex: true,
    propertyName: 'id',
  },
  city: { expr: 'company_property.city', useLike: true },
  // String → substring LIKE (free-text callers, e.g. the public API).
  // JSON array → exact `IN` (the v2 network filter picker, DELTA-2570) —
  // see `buildColumnClause` in companies-list-filter.ts.
  officialName: { expr: 'company_property.official_name', useLike: true },
  connectionType: {
    expr: `(COALESCE((company_profiles.data->'connection_type'->>'value'), (company_profiles.data->'connection_type'->'value')::text))`,
    arrayable: true,
  },
  relationshipTypeIds: {
    expr: `(COALESCE((company_profiles.data->'relationship_type'->>'value'), (company_profiles.data->'relationship_type'->'value')::text))`,
    useJsonRegex: true,
    propertyName: 'id',
  },
  collaborationStatusIds: {
    expr: `(COALESCE((company_profiles.data->'collaboration_status'->>'value'), (company_profiles.data->'collaboration_status'->'value')::text))`,
    arrayable: true,
  },
  companyIds: { expr: 'company_profiles.for_company_id', arrayable: true },
  companySize: { expr: 'company_property.company_size' },
  companyType: { expr: 'company_property.company_type' },
  founded: { expr: 'company_property.founded' },
  isDemoCompany: { expr: 'companies.is_demo_company', arrayable: true },
  supplyChainTier: {
    expr: `(COALESCE((company_profiles.data->'supply_chain_tier'->>'value'), (company_profiles.data->'supply_chain_tier'->'value')::text))`,
    useJsonRegex: true,
    propertyName: null,
  },
  responsiblePerson: {
    expr: `(COALESCE((company_profiles.data->'responsible_person'->>'value'), (company_profiles.data->'responsible_person'->'value')::text))`,
    useJsonRegex: true,
    propertyName: null,
  },
  vendors: {
    expr: `(COALESCE((company_profiles.data->'vendors'->>'value'), (company_profiles.data->'vendors'->'value')::text))`,
    useJsonRegex: true,
    propertyName: null,
  },
  agents: {
    expr: `(COALESCE((company_profiles.data->'agents'->>'value'), (company_profiles.data->'agents'->'value')::text))`,
    useJsonRegex: true,
    propertyName: null,
  },
  allowSharingAddress: {
    expr: 'company_property.allow_sharing_address',
    arrayable: true,
  },
  allowSharingProof: {
    expr: 'company_property.allow_sharing_proof',
    arrayable: true,
  },
  contactPersonEmail: {
    expr: 'company_property.contact_person_email',
    useLike: true,
  },
  enabledCustomInfo: {
    expr: 'company_property.enabled_custom_info',
    arrayable: true,
  },
  internalCompanyCode: {
    expr: 'company_property.internal_company_code',
    useLike: true,
  },
  notes: { expr: 'company_property.notes', useLike: true },
};

/** Mirrors the legacy `PG_SORT_SCOPED` map. */
const SORT_COLUMNS: Record<string, string> = {
  updatedAt: 'company_profiles.updated_at',
  createdAt: 'company_profiles.created_at',
  name: 'LOWER(company_property.display_name)',
  // Ordered by the display name, not the ISO alpha-2 code the column stores —
  // the UI shows the full name, so sorting by code reads as wrong (Germany
  // before Denmark, Austria before Australia). LOWER() matches `name` above.
  country: 'LOWER(company_property.country_name)',
  officialName: 'LOWER(company_property.official_name)',
  city: 'company_property.city',
  collaborationDateStart: 'company_property.collaboration_start_date',
  connectionType: 'connection_type',
  collaborationStatus: 'company_property.collaboration_status',
  facilityProcesses: 'facility_processes',
  riskScore: 'company_property.retraced_risk_score',
  supplyChainTier: 'supply_chain_tier',
  collaborationEndDate: 'company_property.collaboration_end_date',
  responsiblePerson: 'responsible_person',
  vendors: 'vendors',
  agents: 'agents',
  allowSharingAddress: 'company_property.allow_sharing_address',
  allowSharingProof: 'company_property.allow_sharing_proof',
  contactPersonEmail: 'company_property.contact_person_email',
  enabledCustomInfo: 'company_property.enabled_custom_info',
  internalCompanyCode: 'company_property.internal_company_code',
  notes: 'company_property.notes',
  activeUsersCount: 'active_users_count',
  isDemoCompany: 'companies.is_demo_company',
  relationshipType: 'relationship_type',
};

const DEFAULT_ORDER_BY = 'company_profiles.updated_at DESC';

const buildOrderBy = (sort: string | undefined): string => {
  if (!sort?.includes(':')) return DEFAULT_ORDER_BY;
  const [col, dir] = sort.split(':');
  const direction = dir.toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
  const expr = SORT_COLUMNS[col];
  return expr ? `${expr} ${direction}` : DEFAULT_ORDER_BY;
};

const SELECT_FIELDS = `
  company_profiles.for_company_id as id,
  company_profiles.for_company_id as key,
  company_profiles.by_company_id,
  company_profiles.on_behalf_of_company_id,
  company_profiles.profile_type,
  COUNT(company_profiles.by_company_id) OVER () as count,
  companies.created_at as creation_date,
  company_profiles.created_at as connection_date,
  company_profiles.created_at,
  company_profiles.created_by_impersonate_id,
  company_profiles.created_by_user_id,
  company_profiles.created_by_company_id,
  company_profiles.created_by_api_key_id,
  company_profiles.created_by_api_key_label,
  company_profiles.updated_at,
  company_profiles.updated_by_impersonate_id,
  company_profiles.updated_by_impersonate_name,
  company_profiles.updated_by_user_id,
  company_profiles.updated_by_user_name,
  company_profiles.updated_by_company_id,
  company_profiles.updated_by_company_name,
  company_profiles.updated_by_api_key_id,
  company_profiles.updated_by_api_key_label,
  COALESCE(
    (company_profiles.data->'facility_processes'->>'value'),
    (company_profiles.data->'facility_processes'->'value')::text
  ) AS facility_processes,
  COALESCE(
    (company_profiles.data->'relationship_type'->>'value'),
    (company_profiles.data->'relationship_type'->'value')::text
  ) AS relationship_type,
  NULLIF(jsonb_path_query_array(company_profiles.data, '$.image_list.value[0 to 2]')::text, '[]') AS image_list,
  (
    SELECT (cp1.data->'connection_type'->>'value') as connection_type
    FROM companies_service_company_profiles cp1
    WHERE cp1.for_company_id = company_profiles.for_company_id
    AND cp1.by_company_id = $1 AND cp1.on_behalf_of_company_id = $1 AND cp1.profile_type = $2
  ) connection_type,
  (company_profiles.data->'supply_chain_tier'->'value')::text AS supply_chain_tier,
  (company_profiles.data->'responsible_person'->'value')::text as responsible_person,
  (company_profiles.data->'vendors'->'value')::text as vendors,
  (company_profiles.data->'agents'->'value')::text as agents,
  (company_profiles.data->'geojson'->'value')::text as geojson,
  company_property.*,
  COALESCE(user_counts.total_users_count, 0) as total_users_count,
  COALESCE(user_counts.active_users_count, 0) as active_users_count,
  COALESCE(user_counts.invited_users_count, 0) as invited_users_count
`;

const FROM_AND_JOINS = `
  FROM companies_service_company_profiles company_profiles
  JOIN companies_service_companies companies
    ON companies.id = company_profiles.for_company_id AND companies.is_removed = $3
  JOIN companies_service_company_links company_links
    ON company_links.company_id = company_profiles.by_company_id
    AND company_links.other_id = company_profiles.for_company_id
  ${buildProfileColumnsLateral('company_profiles.data')}
  LEFT JOIN (
    SELECT
      uc.company_id,
      COUNT(1) as total_users_count,
      COUNT(CASE WHEN u.has_agreed_with_terms = true OR u.has_agreed_with_last_term_at IS NOT NULL THEN 1 END) as active_users_count,
      COUNT(CASE WHEN uc.invited = 1 THEN 1 END) as invited_users_count
    FROM user_companies uc
    LEFT JOIN users u ON u.id = uc.user_id
    GROUP BY uc.company_id
  ) user_counts ON user_counts.company_id = company_profiles.for_company_id
`;

export const listCompaniesNetworkDb = async (
  db: DbClient,
  companyId: string,
  params: ListCompaniesNetworkParams,
): Promise<ListCompaniesNetworkResult> => {
  const { count, isDropDown, page, sort, hasRiskV3Enabled } = params;

  const bindings: unknown[] = [companyId, 'ABOUT', 0];
  const bind = (value: unknown): string => {
    bindings.push(value);
    return `$${bindings.length}`;
  };

  const filterClause = buildCompaniesListFilterClause(
    params,
    FILTER_COLUMNS,
    bind,
    '$1',
  );
  // Stable tiebreaker on for_company_id keeps LIMIT/OFFSET pagination
  // deterministic when the sort column has ties.
  const orderBy = `${buildOrderBy(sort)}, company_profiles.for_company_id ASC`;

  const baseQuery = `
    SELECT ${SELECT_FIELDS}
    ${FROM_AND_JOINS}
    WHERE company_profiles.profile_type = $2
    AND company_profiles.by_company_id = $1
    AND company_profiles.on_behalf_of_company_id = company_profiles.by_company_id
    ${filterClause}
    ORDER BY ${orderBy}
  `;

  let rawRows: RawCompanyListRow[];
  let pageSize: number;
  if (isDropDown) {
    rawRows = await db.runSql<RawCompanyListRow>(baseQuery, bindings);
    pageSize = rawRows.length;
  } else {
    const limitPh = bind(count);
    const offsetPh = bind(page * count);
    rawRows = await db.runSql<RawCompanyListRow>(
      `${baseQuery} LIMIT (${limitPh})::bigint OFFSET (${offsetPh})::bigint`,
      bindings,
    );
    pageSize = count;
  }

  // `totalCount`/`pageSize` reflect the RAW row count (pre-dedupe) on the
  // dropdown path — matches legacy `listCompaniesDropDown`, which computes
  // them from `companies.length` before `uniqueByKey` is applied.
  const totalCount = isDropDown
    ? rawRows.length
    : rawRows.length > 0 && rawRows[0].count != null
      ? Number(rawRows[0].count)
      : 0;

  const transformed = rawRows.map(row =>
    transformCompanyListRow(row, hasRiskV3Enabled),
  );

  // The dropdown path (unpaginated `db.runSql`, no `db.readPagination`-style
  // LIMIT) can surface the same company more than once when it matches on
  // more than one join path — dedupe defensively, matching legacy
  // `uniqueByKey(companies, 'id')`.
  const rows = isDropDown ? dedupeById(transformed) : transformed;

  return { rows, totalCount, pageSize };
};

/** Ports the legacy `uniqueByKey(rows, 'id')`. */
export const dedupeById = (rows: CompanyListRow[]): CompanyListRow[] => {
  const seen = new Set<string>();
  return rows.filter(row => {
    if (seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
};
